<script lang="ts" setup>
import NavButton from "~/components/NavigationBar/NavButton.vue";
import {ref, watch} from "vue";
import {ActivitiesDetails, EmptyDetails, ServicesDetails, TheRefugeDetails} from "~/data/NavDetails";
import {useWindowScroll} from "@vueuse/core";
import InternalRedirectButton from "~/components/Buttons/InternalRedirectButton.vue";

const localePath = useLocalePath();
const logo_link = "https://res.cloudinary.com/dlct0dnwt/image/upload/v1681256171/refuge/LOGO_AGGLIKO_j6t0ou.svg";

//#region Hovering

const lastHoveredButton = ref("");
const hoveringOverDetails = ref(false);
const selectedDetails = ref(EmptyDetails);
let closeTimer: NodeJS.Timeout | null = null;

const showDetails$ = computed(() => lastHoveredButton.value !== '' || hoveringOverDetails.value);

const detailsMap = {
  refuge: TheRefugeDetails,
  services: ServicesDetails,
  activities: ActivitiesDetails
} as const;

function onHoverEnterFromButton(source: string) {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  
  if (source in detailsMap) {
    lastHoveredButton.value = source;
    selectedDetails.value = detailsMap[source as keyof typeof detailsMap];
  } else {
    // If hovering over a button that doesn't have details (like webcams)
    // we should still keep the previous details open for a bit or close them if it's not a detail-button
    closeDetailsWithDelay();
  }
}

function onHoverExitFromButton() {
  closeDetailsWithDelay();
}

function onHoverEnterDetails() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  hoveringOverDetails.value = true;
}

function onHoverExitDetails() {
  hoveringOverDetails.value = false;
  closeDetailsWithDelay();
}

function closeDetailsNow() {
  if (closeTimer) clearTimeout(closeTimer);
  hoveringOverDetails.value = false;
  lastHoveredButton.value = "";
}

function closeDetailsWithDelay() {
  if (closeTimer) clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    if (!hoveringOverDetails.value) {
      lastHoveredButton.value = "";
    }
  }, 150); // 150ms grace period
}

//#endregion

const {y} = useWindowScroll()
const scrolledFromTop = computed(() => y.value > 50);

const route = useRoute();
watch(
    () => route.path,
    () => {
      lastHoveredButton.value = "";
      if (closeTimer) clearTimeout(closeTimer);
    }
)
</script>

<template>
  <nav class="all-nav-container" :aria-label="$t('mainNavigation')"
       :class="{ 'scrolled' : scrolledFromTop, 'details-open' : showDetails$ }">
    <div class="nav">
      <div class="nav-container">
        <div class="left">
          <div class="logo-wrapper">
            <nuxt-link :to="localePath('/')" class="logo-link" :aria-label="$t('goToHomepage')">
              <nuxt-img :src="logo_link" :alt="$t('refugeLogoAlt')" class="logo"/>
            </nuxt-link>
          </div>

          <ul class="nav-links">
            <li>
              <NavButton :name="$t('The Refuge')" navRoute="refuge" :except="['/refuge/cameras']"
                         has-details :expanded="showDetails$ && lastHoveredButton === 'refuge'"
                         @hoverEnter="onHoverEnterFromButton"
                         @hoverExit="onHoverExitFromButton"/>
            </li>
            <li>
              <NavButton :name="$t('Services')" navRoute="services"
                         has-details :expanded="showDetails$ && lastHoveredButton === 'services'"
                         @hoverEnter="onHoverEnterFromButton"
                         @hoverExit="onHoverExitFromButton"/>
            </li>
            <li>
              <NavButton :name="$t('Activities')" navRoute="activities"
                         has-details :expanded="showDetails$ && lastHoveredButton === 'activities'"
                         @hoverEnter="onHoverEnterFromButton"
                         @hoverExit="onHoverExitFromButton"/>
            </li>
            <li class="separator" aria-hidden="true">|</li>
            <li>
              <NavButton :name="$t('webCams')" navRoute="/refuge/cameras" @hoverEnter="onHoverEnterFromButton"
                         @hoverExit="onHoverExitFromButton"/>
            </li>
          </ul>
        </div>

        <div class="right">
          <InternalRedirectButton :text="$t('Book now')" redirect="/refuge/booking" class="nav-cta" height="2.75rem" icon="ph:calendar-blank" iconSize="1.2rem"/>
          <SiteSearch iconSize="1.35rem"/>
          <LangSwitcher/>
          <ThemeToggle />
        </div>
      </div>
    </div>

    <!--  Details panel that opens when hovering over a button -->
    <Transition name="expand">
      <div v-show="showDetails$" id="nav-details-panel" class="details-panel"
           @mouseenter="onHoverEnterDetails" @mouseleave="onHoverExitDetails"
           @focusin="onHoverEnterDetails" @focusout="onHoverExitDetails"
           @keydown.esc="closeDetailsNow">
        <DetailsView :key="selectedDetails.header" :details="selectedDetails"/>
      </div>
    </Transition>
  </nav>
</template>

<style lang="sass" scoped>
.all-nav-container
  position: fixed
  top: 1.5rem
  left: 50%
  transform: translateX(-50%)
  width: 85vw
  max-width: 1300px
  z-index: 100
  background-color: $color-bg-glass
  backdrop-filter: $blur-standard
  border-radius: $border-radius-standard
  box-shadow: $shadow-modern
  transition: box-shadow 400ms cubic-bezier(0.16, 1, 0.3, 1)
  border: 1px solid rgba(255, 255, 255, 0.3)

  &.scrolled
    box-shadow: $shadow-hover

  &.details-open
    border-bottom-left-radius: 0
    border-bottom-right-radius: 0

.dark-mode .all-nav-container
  background-color: $color-bg-glass-dark
  border: 1px solid rgba(255, 255, 255, 0.1)

.nav
  padding: 0.75rem 2rem
  display: flex
  align-items: center
  justify-content: center

.nav-container
  width: 100%
  display: flex
  justify-content: space-between
  align-items: center

.left
  display: flex
  align-items: center
  gap: 2.5rem

.logo-wrapper
  height: 45px
  display: flex
  align-items: center

.logo-link
  height: 100%
  display: flex
  align-items: center

.logo
  height: 100%
  cursor: pointer
  transition: transform $transition-standard
  &:hover
    transform: scale(1.05)

.nav-links
  display: flex
  align-items: center
  gap: 1.75rem
  list-style: none
  margin: 0
  padding: 0

.separator
  color: rgba(0, 0, 0, 0.1)
  user-select: none

.dark-mode .separator
  color: rgba(255, 255, 255, 0.1)

.right
  display: flex
  align-items: center
  gap: 1.5rem

.nav-cta
  padding: 0 1.75rem
  border-radius: 8px
  font-size: 0.9rem

.details-panel
  background-color: $color-bg-glass
  backdrop-filter: $blur-standard
  width: 100%
  height: 350px
  border-bottom-left-radius: $border-radius-standard
  border-bottom-right-radius: $border-radius-standard
  border-top: 1px solid rgba(0, 0, 0, 0.05)
  display: flex
  justify-content: center
  align-items: center
  overflow: hidden

.dark-mode .details-panel
  background-color: $color-bg-glass-dark
  border-top: 1px solid rgba(255, 255, 255, 0.1)

// Transition animations
.expand-enter-active, .expand-leave-active
  transition: all 400ms cubic-bezier(0.16, 1, 0.3, 1)
  max-height: 350px

.expand-enter-from, .expand-leave-to
  max-height: 0
  opacity: 0

@media (max-width: 1280px)
  .all-nav-container
    width: 92vw

@media (max-width: 1100px)
  .nav
    padding: 0.75rem 1.5rem
  .left
    gap: 2rem
  .nav-links
    gap: 1.25rem
  .right
    gap: 1rem
  .nav-cta
    padding: 0 1.25rem

@media (max-width: 960px)
  .nav
    padding: 0.6rem 1rem
  .left
    gap: 1.25rem
  .logo-wrapper
    height: 38px
  .nav-links
    gap: 0.75rem
  .right
    gap: 0.6rem
  .nav-cta
    padding: 0 0.9rem
</style>
